import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { charactersAPI, dmRequestsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
    ArrowLeft, Plus, Sword, Trash2, Edit, Save, X, Shield, Upload, FileText,
    Users, Search, MapPin, Clock, MessageSquare, Check, Crown, Globe, Home
} from 'lucide-react';

const DND_CLASSES = [
    'Barbarian', 'Bard', 'Cleric', 'Druid', 'Fighter', 'Monk',
    'Paladin', 'Ranger', 'Rogue', 'Sorcerer', 'Warlock', 'Wizard', 'Artificer'
];

const DND_RACES = [
    'Human', 'Elf', 'Dwarf', 'Halfling', 'Dragonborn', 'Gnome',
    'Half-Elf', 'Half-Orc', 'Tiefling', 'Aasimar', 'Goliath', 'Tabaxi'
];

const STATS = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'];

const CAMPAIGN_TYPES = [
    { value: 'homebrew', label: 'Homebrew Campaign' },
    { value: 'official', label: 'Official Module' },
    { value: 'one-shot', label: 'One-Shot Adventure' },
];

const EXPERIENCE_LEVELS = [
    { value: 'any', label: 'Any Level' },
    { value: 'beginner', label: 'Beginner Friendly' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
];

const LOCATION_PREFS = [
    { value: 'online', label: 'Online Only', icon: Globe },
    { value: 'in-person', label: 'In-Person', icon: Home },
    { value: 'hybrid', label: 'Hybrid', icon: MapPin },
];

const DNDTools = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    
    // Tab state
    const [activeTab, setActiveTab] = useState('characters'); // 'characters' | 'find-dm'
    
    // Character state
    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [editing, setEditing] = useState(false);
    
    // DM Request state
    const [dmRequests, setDmRequests] = useState([]);
    const [myDmRequests, setMyDmRequests] = useState([]);
    const [dmLoading, setDmLoading] = useState(false);
    const [showDmRequestModal, setShowDmRequestModal] = useState(false);
    const [selectedDmRequest, setSelectedDmRequest] = useState(null);
    const [showRespondModal, setShowRespondModal] = useState(false);
    const [dmResponseForm, setDmResponseForm] = useState({ message: '', experience: '' });
    
    // Memoized filtered DM requests (excludes current user's requests)
    const otherDmRequests = useMemo(() => 
        dmRequests.filter(r => r.user_id !== user?.user_id),
        [dmRequests, user?.user_id]
    );
    
    // DM Request form
    const [dmRequestForm, setDmRequestForm] = useState({
        title: '',
        description: '',
        campaign_type: 'homebrew',
        experience_level: 'any',
        preferred_schedule: '',
        player_count: 4,
        location_preference: 'online',
    });
    
    const [formData, setFormData] = useState({
        character_name: '',
        character_class: 'Fighter',
        level: 1,
        race: 'Human',
        background: '',
        stats: { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 },
        notes: '',
    });

    // Fetch characters
    useEffect(() => {
        const fetchCharacters = async () => {
            try {
                setLoading(true);
                const response = await charactersAPI.getCharacters();
                setCharacters(response.data);
            } catch (error) {
                console.error('Error fetching characters:', error);
                // Don't show error for 401 - user might not be logged in yet
                if (error.response?.status !== 401) {
                    toast.error('Failed to load characters');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchCharacters();
    }, []);

    // Fetch DM requests
    const fetchDmRequests = useCallback(async () => {
        try {
            setDmLoading(true);
            const [allRequests, myReqs] = await Promise.all([
                dmRequestsAPI.getRequests({ status: 'open' }),
                dmRequestsAPI.getMyRequests()
            ]);
            setDmRequests(allRequests.data);
            setMyDmRequests(myReqs.data);
        } catch (error) {
            console.error('Error fetching DM requests:', error);
            if (error.response?.status !== 401) {
                toast.error('Failed to load DM requests');
            }
        } finally {
            setDmLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'find-dm') {
            fetchDmRequests();
        }
    }, [activeTab, fetchDmRequests]);

    const handleCreate = async () => {
        if (!formData.character_name) {
            toast.error('Character name is required');
            return;
        }

        try {
            const response = await charactersAPI.createCharacter(formData);
            setCharacters([...characters, response.data]);
            toast.success('Character created!');
            setShowCreateModal(false);
            resetForm();
        } catch (error) {
            console.error('Error creating character:', error);
            toast.error('Failed to create character');
        }
    };

    const handleUpdate = async () => {
        if (!selectedCharacter) return;

        try {
            const response = await charactersAPI.updateCharacter(selectedCharacter.sheet_id, formData);
            setCharacters(characters.map(c => 
                c.sheet_id === selectedCharacter.sheet_id ? response.data : c
            ));
            toast.success('Character updated!');
            setEditing(false);
            setSelectedCharacter(response.data);
        } catch (error) {
            console.error('Error updating character:', error);
            toast.error('Failed to update character');
        }
    };

    const handleDelete = async (sheetId) => {
        try {
            await charactersAPI.deleteCharacter(sheetId);
            setCharacters(characters.filter(c => c.sheet_id !== sheetId));
            toast.success('Character deleted');
            setSelectedCharacter(null);
        } catch (error) {
            console.error('Error deleting character:', error);
            toast.error('Failed to delete character');
        }
    };

    const resetForm = () => {
        setFormData({
            character_name: '',
            character_class: 'Fighter',
            level: 1,
            race: 'Human',
            background: '',
            stats: { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 },
            notes: '',
        });
    };

    const openCharacter = (character) => {
        setSelectedCharacter(character);
        setFormData({
            character_name: character.character_name,
            character_class: character.character_class,
            level: character.level,
            race: character.race,
            background: character.background || '',
            stats: character.stats || { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 },
            notes: character.notes || '',
        });
        setEditing(false);
    };

    const getModifier = (stat) => {
        const mod = Math.floor((stat - 10) / 2);
        return mod >= 0 ? `+${mod}` : `${mod}`;
    };

    // DM Request handlers
    const handleCreateDmRequest = async () => {
        if (!dmRequestForm.title || !dmRequestForm.description) {
            toast.error('Please fill in title and description');
            return;
        }

        try {
            await dmRequestsAPI.createRequest(dmRequestForm);
            toast.success('DM request posted!');
            setShowDmRequestModal(false);
            setDmRequestForm({
                title: '',
                description: '',
                campaign_type: 'homebrew',
                experience_level: 'any',
                preferred_schedule: '',
                player_count: 4,
                location_preference: 'online',
            });
            fetchDmRequests();
        } catch (error) {
            console.error('Error creating DM request:', error);
            toast.error('Failed to post DM request');
        }
    };

    const handleRespondToDmRequest = async () => {
        if (!dmResponseForm.message) {
            toast.error('Please write a message');
            return;
        }

        try {
            await dmRequestsAPI.respondToRequest(selectedDmRequest.request_id, dmResponseForm);
            toast.success('Response sent!');
            setShowRespondModal(false);
            setDmResponseForm({ message: '', experience: '' });
            setSelectedDmRequest(null);
            fetchDmRequests();
        } catch (error) {
            console.error('Error responding:', error);
            toast.error(error.response?.data?.detail || 'Failed to send response');
        }
    };

    const handleAcceptDm = async (requestId, dmId) => {
        try {
            await dmRequestsAPI.acceptResponse(requestId, dmId);
            toast.success('DM accepted! You can now contact them.');
            setSelectedDmRequest(null);
            fetchDmRequests();
        } catch (error) {
            console.error('Error accepting DM:', error);
            toast.error('Failed to accept DM');
        }
    };

    const handleDeleteDmRequest = async (requestId) => {
        try {
            await dmRequestsAPI.deleteRequest(requestId);
            toast.success('Request deleted');
            setSelectedDmRequest(null);
            fetchDmRequests();
        } catch (error) {
            console.error('Error deleting request:', error);
            toast.error('Failed to delete request');
        }
    };

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="dnd-tools-page">
            <div className="scanlines" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 border-b border-[#003b00] backdrop-blur-sm">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="text-[#00ff41]"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-[#00ff41] tracking-wider flex items-center gap-2">
                            <Sword className="w-5 h-5" />
                            DND_TOOLS
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        {activeTab === 'characters' ? (
                            <>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".pdf"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        
                                        try {
                                            setImporting(true);
                                            const response = await charactersAPI.importFromPDF(file);
                                            setCharacters([...characters, response.data.character]);
                                            toast.success('Character imported! Please review the data.');
                                            openCharacter(response.data.character);
                                        } catch (error) {
                                            console.error('Import error:', error);
                                            toast.error('Failed to import PDF');
                                        } finally {
                                            setImporting(false);
                                            e.target.value = '';
                                        }
                                    }}
                                    className="hidden"
                                />
                                <Button
                                    data-testid="import-pdf-btn"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={importing}
                                    className="border-[#003b00] text-[#00ff41]"
                                >
                                    {importing ? (
                                        <div className="w-4 h-4 mr-2 border-2 border-[#00ff41] border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Upload className="w-4 h-4 mr-2" />
                                    )}
                                    Import PDF
                                </Button>
                                <Button
                                    data-testid="create-character-btn"
                                    onClick={() => {
                                        resetForm();
                                        setShowCreateModal(true);
                                    }}
                                    className="bg-[#00ff41] text-black"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    New Character
                                </Button>
                            </>
                        ) : (
                            <Button
                                data-testid="find-dm-btn"
                                onClick={() => setShowDmRequestModal(true)}
                                className="bg-[#00ff41] text-black"
                            >
                                <Search className="w-4 h-4 mr-2" />
                                Find a DM
                            </Button>
                        )}
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="container mx-auto px-4 py-2 flex gap-4 border-t border-[#003b00]">
                    <button
                        data-testid="characters-tab"
                        onClick={() => setActiveTab('characters')}
                        className={`px-4 py-2 font-mono text-sm transition-colors ${
                            activeTab === 'characters' 
                                ? 'text-[#00ff41] border-b-2 border-[#00ff41]' 
                                : 'text-[#008f11] hover:text-[#00ff41]'
                        }`}
                    >
                        <Shield className="w-4 h-4 inline mr-2" />
                        Characters
                    </button>
                    <button
                        data-testid="find-dm-tab"
                        onClick={() => setActiveTab('find-dm')}
                        className={`px-4 py-2 font-mono text-sm transition-colors ${
                            activeTab === 'find-dm' 
                                ? 'text-[#00ff41] border-b-2 border-[#00ff41]' 
                                : 'text-[#008f11] hover:text-[#00ff41]'
                        }`}
                    >
                        <Crown className="w-4 h-4 inline mr-2" />
                        Find a DM
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-32 pb-10 px-4">
                <div className="container mx-auto max-w-6xl">
                    {activeTab === 'characters' ? (
                        // Characters Tab Content
                        loading ? (
                            <div className="flex justify-center py-20">
                                <div className="matrix-loader" />
                            </div>
                        ) : characters.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {characters.map((character) => (
                                <motion.div
                                    key={character.sheet_id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="terminal-window cursor-pointer card-hover"
                                    onClick={() => openCharacter(character)}
                                >
                                    <div className="terminal-header">
                                        <div className="terminal-dot red" />
                                        <div className="terminal-dot yellow" />
                                        <div className="terminal-dot green" />
                                        <span className="ml-4 text-[#00ff41] text-sm">character_{character.sheet_id.slice(-6)}.chr</span>
                                    </div>

                                    <div className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-xl font-bold text-[#00ff41]">
                                                    {character.character_name}
                                                </h3>
                                                <p className="text-[#008f11] text-sm">
                                                    Level {character.level} {character.race} {character.character_class}
                                                </p>
                                            </div>
                                            <Shield className="w-8 h-8 text-[#003b00]" />
                                        </div>

                                        <div className="grid grid-cols-6 gap-1">
                                            {STATS.map((stat) => (
                                                <div key={stat} className="stat-block text-center">
                                                    <p className="stat-name">{stat}</p>
                                                    <p className="stat-value text-lg">{character.stats?.[stat] || 10}</p>
                                                    <p className="stat-modifier text-xs">
                                                        {getModifier(character.stats?.[stat] || 10)}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <Sword className="w-16 h-16 text-[#003b00] mx-auto mb-4" />
                            <p className="text-[#008f11] font-mono mb-4">
                                {'>'} No characters found
                            </p>
                            <Button
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="bg-[#00ff41] text-black"
                            >
                                Create Your First Character
                            </Button>
                        </div>
                    )
                    ) : (
                        // Find a DM Tab Content
                        dmLoading ? (
                            <div className="flex justify-center py-20">
                                <div className="matrix-loader" />
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* My Requests Section */}
                                {myDmRequests.length > 0 && (
                                    <div>
                                        <h2 className="text-lg font-bold text-[#00ff41] mb-4 flex items-center gap-2">
                                            <MessageSquare className="w-5 h-5" />
                                            MY_REQUESTS
                                        </h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {myDmRequests.map((request) => (
                                                <motion.div
                                                    key={request.request_id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="terminal-window cursor-pointer card-hover"
                                                    onClick={() => setSelectedDmRequest(request)}
                                                >
                                                    <div className="terminal-header">
                                                        <div className="terminal-dot red" />
                                                        <div className="terminal-dot yellow" />
                                                        <div className="terminal-dot green" />
                                                        <span className="ml-4 text-[#00ff41] text-sm">
                                                            {request.status === 'matched' ? 'MATCHED' : request.status === 'open' ? 'OPEN' : 'CLOSED'}
                                                        </span>
                                                    </div>
                                                    <div className="p-4">
                                                        <h3 className="text-[#00ff41] font-bold mb-2">{request.title}</h3>
                                                        <p className="text-[#008f11] text-sm line-clamp-2 mb-3">{request.description}</p>
                                                        <div className="flex items-center gap-4 text-xs text-[#008f11]">
                                                            <span className="flex items-center gap-1">
                                                                <Users className="w-3 h-3" />
                                                                {request.player_count} players
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <MessageSquare className="w-3 h-3" />
                                                                {request.responses?.length || 0} responses
                                                            </span>
                                                        </div>
                                                        {request.matched_dm_name && (
                                                            <div className="mt-2 bg-[#003b00] px-2 py-1 inline-flex items-center gap-1 text-[#00ff41] text-xs">
                                                                <Check className="w-3 h-3" />
                                                                Matched with {request.matched_dm_name}
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Open Requests Section */}
                                <div>
                                    <h2 className="text-lg font-bold text-[#00ff41] mb-4 flex items-center gap-2">
                                        <Search className="w-5 h-5" />
                                        GROUPS_LOOKING_FOR_DM
                                    </h2>
                                    {otherDmRequests.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {otherDmRequests.map((request) => (
                                                <motion.div
                                                    key={request.request_id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="terminal-window cursor-pointer card-hover"
                                                    onClick={() => {
                                                        setSelectedDmRequest(request);
                                                        setShowRespondModal(true);
                                                    }}
                                                >
                                                    <div className="terminal-header">
                                                        <div className="terminal-dot red" />
                                                        <div className="terminal-dot yellow" />
                                                        <div className="terminal-dot green" />
                                                        <span className="ml-4 text-[#00ff41] text-sm">
                                                            {CAMPAIGN_TYPES.find(t => t.value === request.campaign_type)?.label || request.campaign_type}
                                                        </span>
                                                    </div>
                                                    <div className="p-4">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                {request.user_picture ? (
                                                                    <img 
                                                                        src={request.user_picture} 
                                                                        alt="" 
                                                                        className="w-6 h-6 rounded-full border border-[#003b00]"
                                                                    />
                                                                ) : (
                                                                    <div className="w-6 h-6 rounded-full bg-[#003b00] flex items-center justify-center text-[#00ff41] text-xs">
                                                                        {request.user_name?.charAt(0).toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <span className="text-[#008f11] text-sm">{request.user_name}</span>
                                                            </div>
                                                            {request.location_preference === 'online' && <Globe className="w-4 h-4 text-[#008f11]" />}
                                                            {request.location_preference === 'in-person' && <Home className="w-4 h-4 text-[#008f11]" />}
                                                            {request.location_preference === 'hybrid' && <MapPin className="w-4 h-4 text-[#008f11]" />}
                                                        </div>
                                                        <h3 className="text-[#00ff41] font-bold mb-2">{request.title}</h3>
                                                        <p className="text-[#008f11] text-sm line-clamp-2 mb-3">{request.description}</p>
                                                        <div className="flex flex-wrap items-center gap-2 text-xs text-[#008f11]">
                                                            <span className="bg-[#003b00] px-2 py-0.5">
                                                                {request.player_count} players
                                                            </span>
                                                            <span className="bg-[#003b00] px-2 py-0.5">
                                                                {EXPERIENCE_LEVELS.find(e => e.value === request.experience_level)?.label || request.experience_level}
                                                            </span>
                                                        </div>
                                                        {request.preferred_schedule && (
                                                            <p className="mt-2 text-xs text-[#008f11] flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                {request.preferred_schedule}
                                                            </p>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-10">
                                            <Crown className="w-12 h-12 text-[#003b00] mx-auto mb-4" />
                                            <p className="text-[#008f11] font-mono">
                                                {'>'} No groups currently looking for a DM
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    )}
                </div>
            </main>

            {/* Create Character Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider">CREATE_CHARACTER</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            Build your D&D character sheet
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Character Name</label>
                            <Input
                                data-testid="character-name-input"
                                value={formData.character_name}
                                onChange={(e) => setFormData({...formData, character_name: e.target.value})}
                                placeholder="Enter character name"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Race</label>
                                <Select 
                                    value={formData.race} 
                                    onValueChange={(v) => setFormData({...formData, race: v})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        {DND_RACES.map((race) => (
                                            <SelectItem key={race} value={race} className="text-[#00ff41]">
                                                {race}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Class</label>
                                <Select 
                                    value={formData.character_class} 
                                    onValueChange={(v) => setFormData({...formData, character_class: v})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        {DND_CLASSES.map((cls) => (
                                            <SelectItem key={cls} value={cls} className="text-[#00ff41]">
                                                {cls}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Level</label>
                                <Input
                                    type="number"
                                    value={formData.level}
                                    onChange={(e) => setFormData({...formData, level: parseInt(e.target.value) || 1})}
                                    min={1}
                                    max={20}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Background</label>
                            <Input
                                value={formData.background}
                                onChange={(e) => setFormData({...formData, background: e.target.value})}
                                placeholder="e.g., Soldier, Noble, Outlander"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-2 block">Ability Scores</label>
                            <div className="grid grid-cols-6 gap-2">
                                {STATS.map((stat) => (
                                    <div key={stat} className="text-center">
                                        <label className="text-xs text-[#008f11]">{stat}</label>
                                        <Input
                                            type="number"
                                            value={formData.stats[stat]}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                stats: {
                                                    ...formData.stats,
                                                    [stat]: parseInt(e.target.value) || 10
                                                }
                                            })}
                                            min={1}
                                            max={30}
                                            className="bg-[#050505] border-[#003b00] text-[#00ff41] text-center"
                                        />
                                        <p className="text-xs text-[#008f11]">
                                            {getModifier(formData.stats[stat])}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Notes</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Character backstory, equipment, abilities..."
                                className="w-full h-24 bg-[#050505] border border-[#003b00] text-[#00ff41] p-2 resize-none rounded-none"
                            />
                        </div>

                        <Button
                            data-testid="save-character-btn"
                            onClick={handleCreate}
                            className="w-full bg-[#00ff41] text-black"
                        >
                            CREATE_CHARACTER
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Character Detail Modal */}
            <Dialog open={!!selectedCharacter} onOpenChange={() => setSelectedCharacter(null)}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider flex items-center justify-between">
                            {editing ? 'EDIT_CHARACTER' : selectedCharacter?.character_name}
                            <div className="flex gap-2">
                                {!editing && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setEditing(true)}
                                            className="text-[#00ff41]"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDelete(selectedCharacter?.sheet_id)}
                                            className="text-[#ff0033]"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </DialogTitle>
                    </DialogHeader>

                    {selectedCharacter && (
                        editing ? (
                            <div className="space-y-4 mt-4">
                                <div>
                                    <label className="text-sm text-[#008f11] mb-1 block">Character Name</label>
                                    <Input
                                        value={formData.character_name}
                                        onChange={(e) => setFormData({...formData, character_name: e.target.value})}
                                        className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-sm text-[#008f11] mb-1 block">Race</label>
                                        <Select 
                                            value={formData.race} 
                                            onValueChange={(v) => setFormData({...formData, race: v})}
                                        >
                                            <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                                {DND_RACES.map((race) => (
                                                    <SelectItem key={race} value={race} className="text-[#00ff41]">
                                                        {race}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div>
                                        <label className="text-sm text-[#008f11] mb-1 block">Class</label>
                                        <Select 
                                            value={formData.character_class} 
                                            onValueChange={(v) => setFormData({...formData, character_class: v})}
                                        >
                                            <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                                {DND_CLASSES.map((cls) => (
                                                    <SelectItem key={cls} value={cls} className="text-[#00ff41]">
                                                        {cls}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div>
                                        <label className="text-sm text-[#008f11] mb-1 block">Level</label>
                                        <Input
                                            type="number"
                                            value={formData.level}
                                            onChange={(e) => setFormData({...formData, level: parseInt(e.target.value) || 1})}
                                            min={1}
                                            max={20}
                                            className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm text-[#008f11] mb-2 block">Ability Scores</label>
                                    <div className="grid grid-cols-6 gap-2">
                                        {STATS.map((stat) => (
                                            <div key={stat} className="text-center">
                                                <label className="text-xs text-[#008f11]">{stat}</label>
                                                <Input
                                                    type="number"
                                                    value={formData.stats[stat]}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        stats: {
                                                            ...formData.stats,
                                                            [stat]: parseInt(e.target.value) || 10
                                                        }
                                                    })}
                                                    min={1}
                                                    max={30}
                                                    className="bg-[#050505] border-[#003b00] text-[#00ff41] text-center"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm text-[#008f11] mb-1 block">Notes</label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                        className="w-full h-24 bg-[#050505] border border-[#003b00] text-[#00ff41] p-2 resize-none rounded-none"
                                    />
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        onClick={handleUpdate}
                                        className="flex-1 bg-[#00ff41] text-black"
                                    >
                                        <Save className="w-4 h-4 mr-2" />
                                        SAVE_CHANGES
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setEditing(false)}
                                        className="border-[#003b00] text-[#00ff41]"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4 mt-4">
                                <div className="data-row">
                                    <p className="data-label">Race & Class</p>
                                    <p className="data-value">
                                        Level {selectedCharacter.level} {selectedCharacter.race} {selectedCharacter.character_class}
                                    </p>
                                </div>

                                {selectedCharacter.background && (
                                    <div className="data-row">
                                        <p className="data-label">Background</p>
                                        <p className="data-value">{selectedCharacter.background}</p>
                                    </div>
                                )}

                                <div>
                                    <p className="data-label mb-2">Ability Scores</p>
                                    <div className="grid grid-cols-6 gap-2">
                                        {STATS.map((stat) => (
                                            <div key={stat} className="stat-block">
                                                <p className="stat-name">{stat}</p>
                                                <p className="stat-value">{selectedCharacter.stats?.[stat] || 10}</p>
                                                <p className="stat-modifier">
                                                    {getModifier(selectedCharacter.stats?.[stat] || 10)}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {selectedCharacter.notes && (
                                    <div className="data-row">
                                        <p className="data-label">Notes</p>
                                        <p className="data-value text-sm whitespace-pre-wrap">
                                            {selectedCharacter.notes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )
                    )}
                </DialogContent>
            </Dialog>

            {/* Create DM Request Modal */}
            <Dialog open={showDmRequestModal} onOpenChange={setShowDmRequestModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider">FIND_A_DM</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            Post a request to find a Dungeon Master for your group
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Title</label>
                            <Input
                                data-testid="dm-request-title"
                                value={dmRequestForm.title}
                                onChange={(e) => setDmRequestForm({...dmRequestForm, title: e.target.value})}
                                placeholder="e.g., Looking for DM for weekly campaign"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Description</label>
                            <textarea
                                data-testid="dm-request-description"
                                value={dmRequestForm.description}
                                onChange={(e) => setDmRequestForm({...dmRequestForm, description: e.target.value})}
                                placeholder="Describe what you're looking for, your group's experience level, preferred playstyle..."
                                className="w-full h-24 bg-[#050505] border border-[#003b00] text-[#00ff41] p-2 resize-none rounded-none"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Campaign Type</label>
                                <Select 
                                    value={dmRequestForm.campaign_type}
                                    onValueChange={(v) => setDmRequestForm({...dmRequestForm, campaign_type: v})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        {CAMPAIGN_TYPES.map(t => (
                                            <SelectItem key={t.value} value={t.value} className="text-[#00ff41]">
                                                {t.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Experience Level</label>
                                <Select 
                                    value={dmRequestForm.experience_level}
                                    onValueChange={(v) => setDmRequestForm({...dmRequestForm, experience_level: v})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        {EXPERIENCE_LEVELS.map(e => (
                                            <SelectItem key={e.value} value={e.value} className="text-[#00ff41]">
                                                {e.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Number of Players</label>
                                <Input
                                    type="number"
                                    value={dmRequestForm.player_count}
                                    onChange={(e) => setDmRequestForm({...dmRequestForm, player_count: parseInt(e.target.value) || 4})}
                                    min={1}
                                    max={10}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Location Preference</label>
                                <Select 
                                    value={dmRequestForm.location_preference}
                                    onValueChange={(v) => setDmRequestForm({...dmRequestForm, location_preference: v})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        {LOCATION_PREFS.map(l => (
                                            <SelectItem key={l.value} value={l.value} className="text-[#00ff41]">
                                                {l.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Preferred Schedule</label>
                            <Input
                                value={dmRequestForm.preferred_schedule}
                                onChange={(e) => setDmRequestForm({...dmRequestForm, preferred_schedule: e.target.value})}
                                placeholder="e.g., Weekends, evenings preferred"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <Button
                            data-testid="submit-dm-request-btn"
                            onClick={handleCreateDmRequest}
                            className="w-full bg-[#00ff41] text-black"
                        >
                            POST_REQUEST
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Respond to DM Request Modal */}
            <Dialog open={showRespondModal} onOpenChange={setShowRespondModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider">OFFER_TO_DM</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            Send your offer to be the DM for this group
                        </DialogDescription>
                    </DialogHeader>

                    {selectedDmRequest && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-[#050505] border border-[#003b00] p-4">
                                <h3 className="text-[#00ff41] font-bold">{selectedDmRequest.title}</h3>
                                <p className="text-[#008f11] text-sm mt-2">{selectedDmRequest.description}</p>
                                <div className="flex gap-4 mt-3 text-xs text-[#008f11]">
                                    <span>{selectedDmRequest.player_count} players</span>
                                    <span>{CAMPAIGN_TYPES.find(t => t.value === selectedDmRequest.campaign_type)?.label}</span>
                                    <span>{LOCATION_PREFS.find(l => l.value === selectedDmRequest.location_preference)?.label}</span>
                                </div>
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Your Message</label>
                                <textarea
                                    data-testid="dm-response-message"
                                    value={dmResponseForm.message}
                                    onChange={(e) => setDmResponseForm({...dmResponseForm, message: e.target.value})}
                                    placeholder="Introduce yourself, describe your DM experience and style..."
                                    className="w-full h-24 bg-[#050505] border border-[#003b00] text-[#00ff41] p-2 resize-none rounded-none"
                                />
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">DM Experience</label>
                                <Input
                                    value={dmResponseForm.experience}
                                    onChange={(e) => setDmResponseForm({...dmResponseForm, experience: e.target.value})}
                                    placeholder="e.g., 5 years DMing, ran Curse of Strahd twice"
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>

                            <Button
                                data-testid="send-dm-response-btn"
                                onClick={handleRespondToDmRequest}
                                className="w-full bg-[#00ff41] text-black"
                            >
                                SEND_OFFER
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* View My DM Request Modal (with responses) */}
            <Dialog open={!!selectedDmRequest && !showRespondModal && myDmRequests.some(r => r.request_id === selectedDmRequest?.request_id)} onOpenChange={() => setSelectedDmRequest(null)}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider flex items-center justify-between">
                            {selectedDmRequest?.title}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDmRequest(selectedDmRequest?.request_id)}
                                className="text-[#ff0033]"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </DialogTitle>
                    </DialogHeader>

                    {selectedDmRequest && (
                        <div className="space-y-4 mt-4">
                            <div className="data-row">
                                <p className="data-label">Status</p>
                                <p className={`data-value ${selectedDmRequest.status === 'matched' ? 'text-[#00ff41]' : ''}`}>
                                    {selectedDmRequest.status.toUpperCase()}
                                    {selectedDmRequest.matched_dm_name && ` with ${selectedDmRequest.matched_dm_name}`}
                                </p>
                            </div>

                            <div className="data-row">
                                <p className="data-label">Description</p>
                                <p className="data-value text-sm">{selectedDmRequest.description}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="data-row">
                                    <p className="data-label">Players</p>
                                    <p className="data-value">{selectedDmRequest.player_count}</p>
                                </div>
                                <div className="data-row">
                                    <p className="data-label">Type</p>
                                    <p className="data-value">{CAMPAIGN_TYPES.find(t => t.value === selectedDmRequest.campaign_type)?.label}</p>
                                </div>
                                <div className="data-row">
                                    <p className="data-label">Location</p>
                                    <p className="data-value">{LOCATION_PREFS.find(l => l.value === selectedDmRequest.location_preference)?.label}</p>
                                </div>
                            </div>

                            {/* DM Responses */}
                            <div>
                                <h3 className="text-[#00ff41] font-bold mb-3 flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4" />
                                    DM Offers ({selectedDmRequest.responses?.length || 0})
                                </h3>
                                {selectedDmRequest.responses?.length > 0 ? (
                                    <ScrollArea className="h-60">
                                        <div className="space-y-3 pr-4">
                                            {selectedDmRequest.responses.map((response) => (
                                                <div key={response.dm_id} className="bg-[#050505] border border-[#003b00] p-4">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            {response.dm_picture ? (
                                                                <img 
                                                                    src={response.dm_picture} 
                                                                    alt="" 
                                                                    className="w-8 h-8 rounded-full border border-[#003b00]"
                                                                />
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-full bg-[#003b00] flex items-center justify-center text-[#00ff41]">
                                                                    {response.dm_name?.charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                            <div>
                                                                <p className="text-[#00ff41] font-bold">{response.dm_name}</p>
                                                                {response.experience && (
                                                                    <p className="text-[#008f11] text-xs">{response.experience}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {selectedDmRequest.status === 'open' && (
                                                            <Button
                                                                data-testid={`accept-dm-${response.dm_id}`}
                                                                size="sm"
                                                                onClick={() => handleAcceptDm(selectedDmRequest.request_id, response.dm_id)}
                                                                className="bg-[#00ff41] text-black"
                                                            >
                                                                <Check className="w-4 h-4 mr-1" />
                                                                Accept
                                                            </Button>
                                                        )}
                                                        {selectedDmRequest.matched_dm_id === response.dm_id && (
                                                            <span className="bg-[#003b00] px-2 py-1 text-[#00ff41] text-xs flex items-center gap-1">
                                                                <Check className="w-3 h-3" />
                                                                Selected
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-[#008f11] text-sm">{response.message}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                ) : (
                                    <div className="text-center py-6 bg-[#050505] border border-[#003b00]">
                                        <Crown className="w-8 h-8 text-[#003b00] mx-auto mb-2" />
                                        <p className="text-[#008f11] text-sm">No offers yet. Check back soon!</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DNDTools;
